import { useNavigate, useLocation } from "react-router";
import { Box, Tab, Tabs } from "@mui/material";
import { SettingsLayout } from "../layout/SettingsLayout";
import { useTranslation } from "react-i18next";
import {
    CREATE,
    DELETE,
    MODIFY,
    PUCK_EXTENSION,
    PUCK_PAGE,
    VIEW,
    useFoodpilotContext,
} from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";

interface CMSLayoutProps {
    children: React.ReactNode;
    title?: string;
    isLoading?: boolean;
    isError?: boolean;
    topBar?: React.ReactNode;
}

export const CMSLayout = (props: CMSLayoutProps) => {
    const { children, title, isLoading, isError, topBar } = props;
    const { t } = useTranslation();
    const { canUser } = useFoodpilotContext();
    const navigate = useNavigate();
    const location = useLocation();
    const canAccessCmsPages = canUser(CREATE, PUCK_PAGE) || canUser(MODIFY, PUCK_PAGE) || canUser(DELETE, PUCK_PAGE);

    const tabs = [
        {
            label: t("settings.cms.tabs.pages", "Pages manager"),
            value: "/settings/cms",
            path: "/settings/cms",
            visible: canAccessCmsPages,
        },
        {
            label: t("settings.cms.tabs.dynablocks", "DynaBlock Manager"),
            value: URLHandler.settings.cms.dynablocks(),
            path: URLHandler.settings.cms.dynablocks(),
            visible: canUser(VIEW, PUCK_EXTENSION),
        },
        {
            label: t("settings.cms.tabs.templates", "PDF templates"),
            value: "/settings/cms/templates",
            path: "/settings/cms/templates",
            visible: canAccessCmsPages,
        },
    ];

    // Determine current tab based on path
    const getCurrentTab = () => {
        const currentPath = location.pathname;
        const searchParams = new URLSearchParams(location.search);
        const from = searchParams.get("from");

        // Exact match first
        const exactMatch = tabs.find((tab) => tab.path === currentPath);
        if (exactMatch) return exactMatch.value;

        // For edit/detail pages, match by prefix
        if (currentPath.includes("/dynablocks") || currentPath.includes("/app-blocks")) {
            return URLHandler.settings.cms.dynablocks();
        }

        if (currentPath.includes("/templates")) return "/settings/cms/templates";

        if (from === "templates") return "/settings/cms/templates";

        // Default to pages
        return "/settings/cms";
    };

    const currentTab = getCurrentTab();

    const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
        navigate(newValue);
    };

    return (
        <SettingsLayout
            title={title || t("settings.cms.title", "Content Management")}
            isLoading={isLoading}
            isError={isError}
            topBar={topBar}
        >
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                <Tabs
                    value={currentTab}
                    onChange={handleTabChange}
                    aria-label="CMS navigation tabs"
                    sx={{
                        "& .MuiTab-root": {
                            color: "#393A36",
                            textTransform: "none",
                            fontSize: "1rem",
                            fontWeight: 500,
                        },
                        "& .MuiTab-root.Mui-selected": {
                            color: (theme) => theme.palette.primary[600],
                        },
                        "& .MuiTabs-indicator": {
                            backgroundColor: (theme) => theme.palette.primary[600],
                        },
                    }}
                >
                    {tabs
                        .filter((tab) => tab.visible)
                        .map((tab) => (
                            <Tab key={tab.value} label={tab.label} value={tab.value} />
                        ))}
                </Tabs>
            </Box>
            {children}
        </SettingsLayout>
    );
};
